/**
 * The contact card's field set — ONE form shared by the /contacts page's
 * Add modal and the dock's contact inspector panel, so a new attribute is
 * added in exactly one place. `compact` renders a single-column stack (the
 * inspector's narrow card); the default is the modal's two-column grid.
 */
import { useState } from "react";
import { Button } from "../common/Button";
import { toast } from "../../stores/toast";
import {
	type ContactForm,
	createCategoryOption,
} from "../../lib/contactsApi";
import styles from "./ContactFormFields.module.css";

export function ContactFormFields({ form, setForm, categoryOptions, onCategoryCreated, compact = false, autoFocusName = false }: {
	form: ContactForm;
	setForm: (f: ContactForm) => void;
	categoryOptions: string[];
	/** Called with the new name after a "＋ New category…" landed in the
	 *  collection schema, so the host can refresh its options list. */
	onCategoryCreated: (name: string) => void;
	compact?: boolean;
	autoFocusName?: boolean;
}) {
	const [newCatOpen, setNewCatOpen] = useState(false);
	const [newCat, setNewCat] = useState("");
	const [addingCat, setAddingCat] = useState(false);

	const addCategory = async () => {
		const clean = newCat.trim();
		if (!clean) return;
		setAddingCat(true);
		const error = await createCategoryOption(clean);
		setAddingCat(false);
		if (error) {
			toast.error(error);
			return;
		}
		onCategoryCreated(clean);
		setForm({ ...form, category: clean });
		setNewCat("");
		setNewCatOpen(false);
	};

	const field = (
		key: keyof ContactForm, label: string,
		type: "text" | "email" | "tel" | "date" | "url" = "text",
		wide = false,
	) => (
		<label className={wide && !compact ? styles.fieldWide : styles.field}>
			<span>{label}</span>
			<input
				type={type}
				value={form[key]}
				onChange={(e) => setForm({ ...form, [key]: e.target.value })}
				autoFocus={autoFocusName && key === "name"}
			/>
		</label>
	);

	return (
		<div className={compact ? styles.formStack : styles.formGrid}>
			{field("name", "Name", "text", true)}
			{categoryOptions.length > 0 && (
				<label className={compact ? styles.field : styles.fieldWide}>
					<span>Category</span>
					<select
						value={form.category}
						onChange={(e) => {
							if (e.target.value === "__new__") {
								setNewCatOpen(true);
							} else {
								setForm({ ...form, category: e.target.value });
							}
						}}
					>
						<option value="">—</option>
						{categoryOptions.map((o) => (
							<option key={o} value={o}>{o}</option>
						))}
						{/* A category the schema lost but this card still carries. */}
						{form.category && !categoryOptions.includes(form.category) && (
							<option value={form.category}>{form.category}</option>
						)}
						<option value="__new__">＋ New category…</option>
					</select>
					{newCatOpen && (
						<span className={styles.newCatRow}>
							<input
								type="text"
								placeholder="New category name"
								value={newCat}
								onChange={(e) => setNewCat(e.target.value)}
								onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void addCategory(); } }}
							/>
							<Button size="sm" variant="secondary" loading={addingCat} onClick={() => void addCategory()}>
								Add
							</Button>
						</span>
					)}
				</label>
			)}
			{field("email", "Email", "email")}
			{field("phone", "Phone", "tel")}
			{field("company", "Company")}
			{field("role", "Role")}
			{field("birthday", "Birthday", "date")}
			{field("nickname", "Nickname")}
			{field("pronouns", "Pronouns")}
			{field("relationship", "Relationship")}
			{field("address", "Address", "text", true)}
			{field("website", "Website", "url", true)}
			<label className={styles.fieldWide}>
				<span>Notes</span>
				<textarea
					rows={4}
					value={form.notes}
					onChange={(e) => setForm({ ...form, notes: e.target.value })}
				/>
			</label>
		</div>
	);
}
